// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

import {
    isAdminRoleAssignment,
    type OrganizationOmnisearchListItem,
} from '@anchorbase/pillar-sdk';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import {
    buildCloverOAuthLaunchPath,
    parseCloverOAuthLaunchContext,
    type CloverOAuthLaunchContext,
} from '@/lib/clover/oauth-launch-context';
import { organizationApi } from '@/lib/pillarApi';
import { sessionSensitiveAuthApi } from '@/lib/sessionSensitiveAuthApi';

function buildAuthorizationLaunchUrl(
    context: CloverOAuthLaunchContext,
    organizationUuid: string
): string {
    const searchParams = new URLSearchParams({
        organization_uuid: organizationUuid,
        merchant_id: context.merchantId,
        client_id: context.clientId,
    });
    return `/api/clover/oauth/authorize/launch?${searchParams.toString()}`;
}

export default function CloverOAuthLaunchPage() {
    const [searchParams] = useSearchParams();
    const context = useMemo(() => parseCloverOAuthLaunchContext(searchParams), [searchParams]);
    const [selectedOrganizationUuid, setSelectedOrganizationUuid] = useState<string | null>(null);
    const activeRoleAssignmentQuery = useQuery({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => await sessionSensitiveAuthApi.getActiveRoleAssignment(),
        retry: false,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
    });
    const activeRoleAssignment = activeRoleAssignmentQuery.data ?? null;
    const canAuthorizeClover = isAdminRoleAssignment(activeRoleAssignment);
    const activeMerchantOrganizationUuid =
        activeRoleAssignment?.organization_is_merchant === true
            ? activeRoleAssignment.organization_uuid
            : null;
    const organizationUuid = activeMerchantOrganizationUuid ?? selectedOrganizationUuid;

    if (context === null) {
        return (
            <main className="flex min-h-screen items-center justify-center p-4">
                <Card className="w-full max-w-lg">
                    <CardHeader>
                        <CardTitle>Invalid Clover launch</CardTitle>
                        <CardDescription>
                            The Clover launch did not include the required merchant context.
                        </CardDescription>
                    </CardHeader>
                </Card>
            </main>
        );
    }

    return (
        <main className="flex min-h-screen items-center justify-center p-4">
            <Card className="w-full max-w-lg">
                <CardHeader>
                    <CardTitle>Connect Clover</CardTitle>
                    <CardDescription>
                        Choose the merchant organization that should be authorized for this Clover
                        account.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {activeRoleAssignmentQuery.isError && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                Unable to determine whether this role can connect Clover.
                            </AlertDescription>
                        </Alert>
                    )}
                    {activeRoleAssignmentQuery.isSuccess && !canAuthorizeClover && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                An organizational administrator or higher role is required to
                                connect Clover.
                            </AlertDescription>
                        </Alert>
                    )}
                    {canAuthorizeClover && activeMerchantOrganizationUuid === null && (
                        <OmnisearchSelect<OrganizationOmnisearchListItem, string>
                            label="Merchant organization"
                            placeholder="Search merchant organizations..."
                            pageSize={50}
                            selectedItemId={selectedOrganizationUuid}
                            onClearSelection={() => setSelectedOrganizationUuid(null)}
                            fetchPage={async (params) => {
                                const response = await organizationApi.searchOrganizations({
                                    ...(params.terms !== undefined
                                        ? { terms: [...params.terms] }
                                        : {}),
                                    is_merchant: true,
                                    limit: params.limit,
                                    cursor: params.cursor,
                                });
                                return {
                                    items: response.organizations,
                                    pagination: response.pagination,
                                };
                            }}
                            getItemId={(item) => item.organization_uuid}
                            getItemLabel={(item) => `${item.name} (${item.organization_uuid})`}
                            getItemFilterText={(item) =>
                                `${item.name} ${item.organization_uuid}`.toLowerCase()
                            }
                            onSelect={(item) => setSelectedOrganizationUuid(item.organization_uuid)}
                        />
                    )}
                    <Button
                        type="button"
                        className="w-full"
                        disabled={!canAuthorizeClover || organizationUuid === null}
                        onClick={() => {
                            if (organizationUuid !== null) {
                                window.location.assign(
                                    buildAuthorizationLaunchUrl(context, organizationUuid)
                                );
                            }
                        }}
                    >
                        Connect Clover
                    </Button>
                    <a
                        className="text-sm text-muted-foreground underline"
                        href={buildCloverOAuthLaunchPath(context)}
                    >
                        Restart this Clover launch
                    </a>
                </CardContent>
            </Card>
        </main>
    );
}
